import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { FilterModel, FilterValue } from '@packages/data-grid';
import { FilterConfig } from '@packages/filter';
import { SearchAndFilterAndSort } from '../../models/query/searchAndFilterAndSort.model';
import { DateService } from '../date/date.service';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private dateService = inject(DateService);

  constructor() {}
  /**
   * @description
   * This function is used to filter an array of object by an attribute of type string
   * @param {string} cat
   * @param {object} item
   * @param {string[]} vArr(string array of values)
   * @returns {boolean}
   */
  filterByString<T>(cat: keyof T, item: T, vArr: string[]): boolean {
    let result: boolean[] = [];
    for (let v of vArr) {
      String(item[cat]).toLowerCase().includes(v.toLowerCase()) ? result.push(true) : result.push(false);
    }
    return result.includes(true) ? true : false;
  }
  /**
   * @description
   * This function is used to filter an array of object by an attribute of type number
   * @param {string} cat
   * @param {object} item
   * @param {number[]} vArr(number array of values)
   * @returns {boolean}
   */
  filterByNumber<T>(cat: keyof T, item: T, vArr: number[]): boolean {
    let result: boolean[] = [];
    for (let v of vArr) {
      Number(item[cat]) === v ? result.push(true) : result.push(false);
    }
    return result.includes(true) ? true : false;
  }
  /**
   * @description
   * This function is used to filter an array of object by a date range
   * @param {string} cat
   * @param {object} item
   * @param {{ dateStart: string, dateEnd: string }[]} vArr(date range array of values)
   * @returns {boolean}
   */
  filterByDateRange<T>(cat: keyof T, item: T, vArr: { dateStart: string; dateEnd: string }[]): boolean {
    let result: boolean[] = [];
    for (let v of vArr) {
      let vStart = new Date(v.dateStart).getTime();
      let vEnd = new Date(v.dateEnd).getTime();
      let itemDate = new Date(String(item[cat])).getTime();
      if (itemDate >= vStart && itemDate <= vEnd) {
        result.push(true);
      } else {
        result.push(false);
      }
    }
    return result.includes(true) ? true : false;
  }
  /**
   * @description
   * This function is used to generate a formated object for query params
   * @param {FilterConfig.FullFilterItemConfig[]} filterItem (filter config)
   * @param {FilterConfig.FullFilterItemConfig[]} currentFilterList (current filter list)
   * @returns {Params}
   */
  getQueryParams(filterItem: FilterConfig.FullFilterItemConfig[], currentFilterList: FilterConfig.FullFilterItemConfig[]): Params {
    let params: Params = {
      current_page: 1,
    };
    filterItem.forEach((item) => {
      params[item.cat] =
        currentFilterList.find((data) => data.cat === item.cat) != undefined ? currentFilterList.find((data) => data.cat === item.cat)!.values : null;
    });
    return params;
  }
  /**
   * @description
   * This function is used to get the current filter list from the route query params
   * @param {ActivatedRouteSnapshot} route
   * @param {FilterConfig.FullFilterItemConfig[]} filterConfig (filter config)
   * @returns {FilterConfig.FullFilterItemConfig[]}
   */
  getCurrentFilterList(route: ActivatedRouteSnapshot, filterConfig: FilterConfig.FullFilterItemConfig[]): FilterConfig.FullFilterItemConfig[] {
    let currentFilterList: FilterConfig.FullFilterItemConfig[] = [];
    for (let filter of filterConfig) {
      if (route.queryParams[filter.cat] && route.queryParams[filter.cat] != null) {
        currentFilterList.push({
          label: filter.label,
          cat: filter.cat,
          values: route.queryParams[filter.cat] instanceof Array ? route.queryParams[filter.cat] : [route.queryParams[filter.cat]],
          type: filter.type,
          promoted: filter.promoted,
        });
      }
    }
    return currentFilterList;
  }
  getFormatedFilterForQuery(filter: FilterConfig.FullFilterItemConfig[]): SearchAndFilterAndSort.FilterItemRequest {
    let formatedFilter: SearchAndFilterAndSort.FilterItemRequest = {};
    for (let f of filter) {
      // TODO: Add more filter types
      if (f.type === 'date') {
        const values = f.values[0]!.split('|');
        f.values = [`${this.formatDateValueForQuery(values[0], 'start')}|${this.formatDateValueForQuery(values[1], 'end')}`];
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values.toString(),
        };
      }
      if (f.type === 'numeric_range') {
        const values = f.values[0]!.split('|');
        f.values = [`${values[0]}|${values[1]}`];
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values.toString(),
        };
      }
      if (f.type === 'check') {
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values.toString(),
        };
      }
      if (f.type === 'list') {
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values.toString(),
        };
      }
      if (f.type === 'nullOrNot') {
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values[0]!.toLocaleLowerCase() === 'avec' ? 'true' : 'false',
        };
      }
      if (f.type === 'trueOrFalse') {
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values[0]!.toLocaleLowerCase(),
        };
      }
      if (f.type === 'only') {
        formatedFilter = {
          ...formatedFilter,
          [`${f.type}::${f.cat}`]: f.values.toString(),
        };
      }
    }
    return formatedFilter;
  }

  getColumnIdForFilterCat(cat: string, columnIdToFilterCatMap: Record<string, string>): string | undefined {
    return Object.keys(columnIdToFilterCatMap).find((columnId) => columnIdToFilterCatMap[columnId] === cat);
  }

  mapGridFilterModelToFrontFilter(
    filterModel: FilterModel,
    filterConfig: FilterConfig.FullFilterItemConfig[],
    columnIdToFilterCatMap: Record<string, string>,
  ): FilterConfig.FullFilterItemConfig[] {
    const result: FilterConfig.FullFilterItemConfig[] = [];
    for (const config of filterConfig) {
      const columnId = this.getColumnIdForFilterCat(config.cat, columnIdToFilterCatMap) ?? config.cat;
      const filterValue: FilterValue | undefined = filterModel[columnId];
      if (!filterValue) continue;
      const values = this.getFrontValuesFromGridFilter(filterValue, config.type);
      if (values.length) result.push({ ...config, values });
    }
    return result;
  }

  mapFrontFilterToGridFilterModel(activeFilters: FilterConfig.FullFilterItemConfig[], columnIdToFilterCatMap: Record<string, string>): FilterModel {
    const result: FilterModel = {};
    for (const config of activeFilters) {
      const columnId = this.getColumnIdForFilterCat(config.cat, columnIdToFilterCatMap) ?? config.cat;
      if (!config.values.length) continue;
      const filter = this.getGridFilterFromFrontFilter(config);
      if (filter) result[columnId] = filter;
    }
    return result;
  }

  private getFrontValuesFromGridFilter(filterValue: FilterValue, frontFilterType: FilterConfig.FilterItemConfig['type']): string[] {
    if (filterValue.type === 'select') return filterValue.value == null ? [] : [String(filterValue.value)];
    if (filterValue.type === 'custom')
      return Array.isArray(filterValue.value) ? filterValue.value.map(String) : this.toStringValues(filterValue.value);
    if (filterValue.type === 'text') return filterValue.value ? [filterValue.value] : [];

    if (filterValue.type === 'number') {
      if (frontFilterType === 'numeric_range') return this.getFrontNumericRangeValues(filterValue);
      return filterValue.value === undefined ? [] : [String(filterValue.value)];
    }

    return this.getFrontDateValues(filterValue);
  }

  private getGridFilterFromFrontFilter(config: FilterConfig.FullFilterItemConfig): FilterValue | undefined {
    if (config.type === 'date') return this.getGridDateFilter(config.values[0]);
    if (config.type === 'numeric_range') return this.getGridNumericRangeFilter(config.values[0]);
    if (['list', 'check', 'only'].includes(config.type)) {
      return config.values.length === 1 ? { type: 'select', value: config.values[0] } : { type: 'custom', value: config.values };
    }
    if (config.type === 'nullOrNot' || config.type === 'trueOrFalse') return { type: 'select', value: config.values[0] };
    return undefined;
  }

  private getFrontDateValues(filterValue: Extract<FilterValue, { type: 'date' }>): string[] {
    if (filterValue.operator === 'between' && filterValue.value && filterValue.valueTo)
      return [`${this.formatDateValueForQuery(filterValue.value, 'start')}|${this.formatDateValueForQuery(filterValue.valueTo, 'end')}`];
    if (filterValue.operator === 'equals' && filterValue.value)
      return [`${this.formatDateValueForQuery(filterValue.value, 'start')}|${this.formatDateValueForQuery(filterValue.value, 'end')}`];
    if (filterValue.operator === 'before' && filterValue.value)
      return [`${this.dateService.get1970Date()}|${this.formatDateValueForQuery(filterValue.value, 'end')}`];
    if (filterValue.operator === 'after' && filterValue.value)
      return [`${this.formatDateValueForQuery(filterValue.value, 'start')}|${this.dateService.getEndTodayDate()}`];
    return [];
  }

  private getFrontNumericRangeValues(filterValue: Extract<FilterValue, { type: 'number' }>): string[] {
    if (filterValue.operator === 'between' && filterValue.value !== undefined && filterValue.valueTo !== undefined) {
      return [`${filterValue.value}|${filterValue.valueTo}`];
    }
    if ((filterValue.operator === 'equals' || filterValue.operator === 'gte' || filterValue.operator === 'lte') && filterValue.value !== undefined) {
      return [`${filterValue.value}|${filterValue.value}`];
    }
    return [];
  }

  private getGridDateFilter(value: string | undefined): Extract<FilterValue, { type: 'date' }> | undefined {
    const [start, end] = this.splitRange(value);
    if (!start || !end) return undefined;
    if (start === end) return { type: 'date', operator: 'equals', value: start };
    if (start === this.dateService.get1970Date()) return { type: 'date', operator: 'before', value: end };
    if (end === this.dateService.getEndTodayDate()) return { type: 'date', operator: 'after', value: start };
    return { type: 'date', operator: 'between', value: start, valueTo: end };
  }

  private getGridNumericRangeFilter(value: string | undefined): Extract<FilterValue, { type: 'number' }> | undefined {
    const [start, end] = this.splitRange(value);
    const startValue = start === undefined || start === '' ? undefined : Number(start);
    const endValue = end === undefined || end === '' ? undefined : Number(end);
    if (startValue === undefined || endValue === undefined || Number.isNaN(startValue) || Number.isNaN(endValue)) return undefined;
    if (startValue === endValue) return { type: 'number', operator: 'equals', value: startValue };
    return { type: 'number', operator: 'between', value: startValue, valueTo: endValue };
  }

  private splitRange(value: string | undefined): [string | undefined, string | undefined] {
    const [start, end] = (value ?? '').split('|');
    return [start, end];
  }

  private formatDateValueForQuery(value: string | undefined, boundary: 'start' | 'end'): string {
    if (!value) return '';
    const trimmedValue = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return `${trimmedValue} ${boundary === 'start' ? '00:00:00' : '23:59:59'}`;
    }
    if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(trimmedValue)) {
      return trimmedValue.length === 16 ? `${trimmedValue}:00` : trimmedValue;
    }
    return this.dateService.convertDateTime(trimmedValue);
  }

  private toStringValues(value: unknown): string[] {
    if (value === undefined || value === null || value === '') return [];
    return [String(value)];
  }
}
